import axiosInstance from '../../config/axios';

// إنشاء نسخة مستقلة من Axios للإعدادات
const settingsAxios = axiosInstance;

// إضافة interceptor لإضافة token المصادقة
settingsAxios.interceptors.request.use(
    (config) => {
        const token = sessionStorage.getItem('token');
        if (token) {
            // تأكد من إضافة Bearer prefix
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// إضافة interceptor للتعامل مع أخطاء الاستجابة
settingsAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        // تجنب تسجيل الخروج إذا كان الخطأ من صفحات معينة
        const ignoredPaths = ['/api/settings/seo', '/api/settings/maintenance'];
        
        // إنشاء كائن تشخيصي لعرض معلومات عن الخطأ
        const errorInfo = {
            status: error.response?.status,
            url: error.config.url,
            isPublicPath: ignoredPaths.some(path => error.config.url.includes(path)),
            isIgnoredPath: ignoredPaths.some(path => error.config.url.includes(path)),
            data: error.response?.data
        };
        
        // حدث خطأ في الطلب
        
        // تسجيل الخروج فقط مع خطأ 401 (غير مصرح) وليس مع خطأ 403 (غير مسموح)
        if (error.response?.status === 401 &&
            !ignoredPaths.some(path => error.config.url.includes(path))) {
            localStorage.removeItem('token');
            window.location.href = '/login';
        }
        
        // إظهار رسالة تشخيصية مع خطأ 403 بدون تسجيل خروج
        if (error.response?.status === 403) {
            // 403 Forbidden - لا يوجد صلاحية للوصول
        }
        
        return Promise.reject(error);
    }
);

export const settingsApi = {
    // جلب إعدادات الصيانة
    getMaintenanceSettings: async () => {
        const response = await settingsAxios.get('/api/settings/maintenance');
        return response.data;
    },

    // تحديث إعدادات الصيانة
    updateMaintenanceSettings: async (data) => {
        const response = await settingsAxios.put('/api/admin/settings/maintenance', data);
        return response.data;
    },

    // جلب إعدادات طرق الدفع
    getPaymentSettings: async () => {
        try {
            const response = await settingsAxios.get('/api/settings/payment');
            return response.data;
        } catch (error) {
            // Error fetching payment settings
            throw error;
        }
    },

    // تحديث إعدادات طرق الدفع
    updatePaymentSettings: async (data) => {
        try {
            const response = await settingsAxios.put('/api/admin/settings/payment', data);
            return response.data;
        } catch (error) {
            // Error updating payment settings
            throw error;
        }
    },

    // حفظ حساب بنكي جديد
    saveBankAccount: async (accountData) => {
        try {
            const formData = new FormData();
            Object.keys(accountData).forEach(key => {
                formData.append(key, accountData[key]);
            });

            const response = await settingsAxios.post('/api/admin/bank-accounts', formData);
            return response.data;
        } catch (error) {
            // Error saving bank account
            throw error;
        }
    },

    // جلب حسابات البنوك
    getBankAccounts: async () => {
        try {
            const response = await settingsAxios.get('/api/admin/bank-accounts');
            return response.data;
        } catch (error) {
            // Error fetching bank accounts
            throw error;
        }
    },

    // حذف حساب بنكي
    deleteBankAccount: async (accountId) => {
        try {
            const response = await settingsAxios.delete(`/api/admin/bank-accounts/${accountId}`);
            return response.data;
        } catch (error) {
            // Error deleting bank account
            throw error;
        }
    },

    // تحديث حساب بنكي
    updateBankAccount: async (accountId, accountData) => {
        try {
            const formData = new FormData();
            Object.keys(accountData).forEach(key => {
                formData.append(key, accountData[key]);
            });

            const response = await settingsAxios.put(`/api/admin/bank-accounts/${accountId}`, formData);
            return response.data;
        } catch (error) {
            // Error updating bank account
            throw error;
        }
    },

    // جلب إعدادات SEO
    getSeoSettings: async () => {
        try {
            const response = await settingsAxios.get('/api/settings/seo');
            return response.data;
        } catch (error) {
            // Error fetching SEO settings
            throw error;
        }
    },

    // تحديث إعدادات SEO
    updateSeoSettings: async (data) => {
        try {
            // Updating SEO settings with data

            const response = await settingsAxios.put('/api/admin/settings/seo', {
                siteTitle: data.siteTitle,
                homePageTitle: data.homePageTitle,
                siteDescription: data.siteDescription,
                keywords: data.keywords,
                socialLinks: data.socialLinks
            });
            return response.data;
        } catch (error) {
            // Error updating SEO settings
            throw error;
        }
    },

    // إضافة منصة جديدة
    addSocialPlatform: async (platformData) => {
        try {
            const response = await settingsAxios.post('/api/admin/settings/seo/platforms', platformData);
            return response.data;
        } catch (error) {
            // Error adding social platform
            throw error;
        }
    },

    // تعديل منصة موجودة
    updateSocialPlatform: async (platformId, platformData) => {
        try {
            const response = await settingsAxios.patch(`/api/admin/settings/seo/platforms/${platformId}`, platformData);
            return response.data;
        } catch (error) {
            // Error updating social platform
            throw error;
        }
    },

    // حذف منصة
    deleteSocialPlatform: async (platformId) => {
        try {
            const response = await settingsAxios.delete(`/api/admin/settings/seo/platforms/${platformId}`);
            return response.data;
        } catch (error) {
            // Error deleting social platform
            throw error;
        }
    },

    // تفعيل/إلغاء تفعيل منصة
    toggleSocialPlatform: async (platformId, enabled) => {
        try {
            const response = await settingsAxios.patch(`/api/admin/settings/seo/platforms/${platformId}/toggle`, { enabled });
            return response.data;
        } catch (error) {
            // Error toggling social platform
            throw error;
        }
    }
};
